import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { DirectionsRun, HourglassBottom, ChevronRight, PlayArrow } from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { formatDuration } from 'components/play/workoutEstimate';
import { selectWorkoutActive, selectWorkoutSession, workoutElapsedSeconds } from 'store/modules/playWorkout';
import { usePlayWorkoutTimer, useWorkoutTick } from 'hooks/play/usePlayWorkoutTimer';
import routes from 'common/routes';

/**
 * OTRIX Play — barra flotante del entrenamiento en curso. Montada en el layout, se ve en TODAS
 * las pantallas (persiste al navegar). Aquí corre el piloto automático del cronómetro
 * (`usePlayWorkoutTimer`) que avanza los descansos aunque el runner no esté montado.
 */
function PlayWorkoutMiniBar() {
  const { t } = useTranslation(['play']);
  const navigate = useNavigate();
  const location = useLocation();

  // Piloto automático (auto-avance de descansos) — corre siempre que haya sesión.
  usePlayWorkoutTimer();

  const active = useSelector(selectWorkoutActive);
  const session = useSelector(selectWorkoutSession);
  const now = useWorkoutTick(active, 1000);

  // No mostrar si no hay entrenamiento o si ya estamos en el runner.
  if (!active || !session || location.pathname === routes.playWorkout.path) {
    return null;
  }

  const isRest = session.phase === 'rest';
  const isPaused = session.phase === 'paused';
  // 'ready' = preparado pero sin dar Play: el reloj aún no corre, no digas "entrenando".
  const isReady = session.phase === 'ready';
  const exercise = session.exercises[session.currentExerciseIndex] || null;

  const phaseKey = (() => {
    if (isReady) return 'miniBar.ready';
    if (isPaused) return 'workout.paused';
    if (isRest) return 'miniBar.resting';
    return 'miniBar.working';
  })();
  const phaseLabel = t(phaseKey, { ns: 'play' });

  const phaseIcon = (() => {
    if (isRest) return <HourglassBottom />;
    if (isReady) return <PlayArrow />;
    return <DirectionsRun />;
  })();

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.appBar + 2,
        left: { xs: 12, md: 'auto' },
        right: { xs: 12, md: 24 },
        // Vuelve a haber barra inferior en móvil (MobileTabBar, 56px + safe area): sin este
        // despeje la mini-barra del entrenamiento le quedaba encima y tapaba los iconos.
        bottom: { xs: 'calc(72px + env(safe-area-inset-bottom, 0px))', lg: 24 },
        maxWidth: 360,
        ml: { xs: 'auto', md: 0 }
      }}>
      <Box
        sx={{
          p: 1.25,
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'gold.main',
          // SÓLIDA a propósito: la barra flota sobre el contenido y `surface.overlay` es
          // translúcido (rgba .04) → el texto de la página se traspasaba y no se leía el crono.
          backgroundColor: 'surface.raised',
          backgroundImage: 'none',
          boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow-hover)'
        }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flex: 'none',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'gold.main',
              backgroundColor: 'gold.soft'
            }}>
            {phaseIcon}
          </Box>
          <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }} noWrap>
              {phaseLabel}
              {exercise?.name ? ` · ${exercise.name}` : ''}
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
              {formatDuration(workoutElapsedSeconds(session, now))}
            </Typography>
          </Stack>
          <Button
            size="small"
            endIcon={<ChevronRight />}
            onClick={() => navigate(routes.playWorkout.path)}
            sx={{ ...premiumButtonSx, py: 0.75, px: 1.5, fontSize: '0.8rem', flex: 'none' }}>
            {t('miniBar.resume', { ns: 'play' })}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

export default PlayWorkoutMiniBar;
